import React from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../../components/AppIcon';
import { RefereeCertification, MatchRole, resolveEnum } from '@/models/tournament';
import type { MatchView } from '@/models/tournament';
import { parseApiWallClock } from '@/utils/apiDate';
import { appDate, appTime, appWeekday } from '@/utils/appDateFormat';
import { useListboxKeyboard } from '../hooks/useListboxKeyboard';

interface MatchDetailsPanelProps {
  matches: MatchView[];
  selectedMatch: MatchView | null;
  onSelectMatch: (match: MatchView) => void;
}

const MatchDetailsPanel: React.FC<MatchDetailsPanelProps> = ({ matches, selectedMatch, onSelectMatch }) => {
  const { t } = useTranslation('tournament');

  // Roving tab stop starts on the currently-selected match (see useListboxKeyboard).
  const selectedIndex = matches.findIndex((match) => match.id === selectedMatch?.id);
  const selectByIndex = React.useCallback(
    (index: number) => {
      const match = matches[index];
      if (match) onSelectMatch(match);
    },
    [matches, onSelectMatch],
  );
  const { getOptionProps } = useListboxKeyboard(matches.length, selectByIndex, selectedIndex);

  const certificationKeys: Record<RefereeCertification, string> = {
    [RefereeCertification.NationalLevelA]: 'National Level A',
    [RefereeCertification.AFCElite]: 'AFC Elite',
    [RefereeCertification.FIFAInternational]: 'FIFA International',
  };

  const roleKeys: Record<MatchRole, string> = {
    [MatchRole.HeadReferee]: 'Head Referee',
    [MatchRole.AssistantReferee]: 'Assistant Referee',
    [MatchRole.FourthOfficial]: 'Fourth Official',
    [MatchRole.Commissioner]: 'Commissioner',
  };

  const getStatusColor = (match: MatchView): string => {
    if (match.refereeTeamMembers?.length > 0) {
      return 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300';
    }
    return 'bg-muted text-muted-foreground';
  };

  const getStatusLabel = (match: MatchView): string => {
    const count = match.refereeTeamMembers?.length ?? 0;
    return count > 0 ? t('Assigned ({{count}})', { count }) : t('Unassigned');
  };

  const formatDate = (dateString: string): string => {
    const date = parseApiWallClock(dateString) ?? new Date(dateString);
    return `${appWeekday(date, 'short')} ${appDate(date)}`;
  };

  const formatTime = (dateString: string): string => {
    const date = parseApiWallClock(dateString) ?? new Date(dateString);
    return appTime(date);
  };

  return (
    <div className="space-y-6">
      {/* Match Selection */}
      <div className="bg-card border border-border rounded-lg p-4" data-testid="match-selection-panel">
        <h2 id="match-select-heading" className="text-lg font-semibold text-foreground flex items-center mb-4">
          <Icon name="Calendar" size={20} className="me-2 text-primary" />
          {t('Select Match to Assign')}
        </h2>

        {matches.length === 0 ? (
          /* An empty match list used to render as an empty scroll box with no message at all, so a
             legitimate zero looked identical to a broken fetch. */
          <div className="py-10 text-center" data-testid="match-select-empty-state">
            <Icon name="CalendarX" size={40} className="mx-auto text-muted-foreground/40 mb-3" />
            <h3 className="text-base font-semibold text-foreground mb-1">{t('No matches found')}</h3>
            <p className="text-sm text-muted-foreground">{t('No matches are available for referee assignment yet.')}</p>
          </div>
        ) : (
          /* Second of the page's two primary selectors, and it had the same problem as the referee
             list: <div onClick> with no role, tabIndex, key handler or aria-selected — and then the
             same half-fix, roles plus an Enter/Space handler and nothing else. Full keyboard
             contract (arrows, Home/End, roving tab stop) is in ../hooks/useListboxKeyboard.
             The nested 500px scroller is `lg`-only so a phone scrolls the page, not a box in a box. */
          <div
            role="listbox"
            aria-labelledby="match-select-heading"
            className="space-y-3 lg:max-h-[500px] lg:overflow-y-auto lg:pe-2"
          >
            {matches.map((match, index) => {
              const isSelected = selectedMatch?.id === match.id;
              return (
                <div
                  key={match.id}
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => onSelectMatch(match)}
                  {...getOptionProps(index)}
                  className={`border rounded-lg p-4 cursor-pointer transition-colors duration-200 motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ${
                    isSelected
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/50 hover:bg-muted/40'
                  }`}
                  data-testid={`match-select-card-${match.id}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <p className="text-xs text-muted-foreground mb-1">{match.tournamentName}</p>
                      <p className="text-sm font-semibold text-foreground">
                        {match.team1Name} <span className="text-muted-foreground/70">{t('vs')}</span> {match.team2Name}
                      </p>
                    </div>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(match)}`}>
                      {getStatusLabel(match)}
                    </span>
                  </div>

                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground mb-2">
                    <div className="flex items-center">
                      <Icon name="Calendar" size={12} className="me-1 text-muted-foreground/70" />
                      <span>{formatDate(match.scheduledDateTime)}</span>
                    </div>
                    <div className="flex items-center">
                      <Icon name="Clock" size={12} className="me-1 text-muted-foreground/70" />
                      <span>{formatTime(match.scheduledDateTime)}</span>
                    </div>
                  </div>

                  <div className="flex items-center text-xs text-muted-foreground">
                    <Icon name="MapPin" size={12} className="me-1 text-muted-foreground/70" />
                    <span className="truncate">{match.location}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Selected Match Details */}
      {selectedMatch && (
        <div className="bg-card border border-border rounded-lg p-6" data-testid="match-details-panel">
          <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
            <Icon name="FileText" size={20} className="me-2 text-primary" />
            {t('Match Details')}
          </h3>

          {/* Match Info. The `from-primary/10 to-blue-50` wash leaned on the exact primary hue, which
              is overridden per tenant at runtime — a tenant with a green primary got a green-to-baby-
              blue gradient. Neutral surface instead. */}
          <div className="bg-muted/50 rounded-lg p-4 mb-4">
            <div className="flex items-center justify-center gap-6 mb-3">
              <div className="text-center">
                <div className="w-12 h-12 bg-card rounded-full flex items-center justify-center mb-2 mx-auto shadow-sm">
                  <Icon name="Shield" size={24} className="text-primary" />
                </div>
                <p className="text-sm font-bold text-foreground">{selectedMatch.team1Name}</p>
              </div>
              <div className="text-xl font-bold text-muted-foreground/70">{t('VS')}</div>
              <div className="text-center">
                <div className="w-12 h-12 bg-card rounded-full flex items-center justify-center mb-2 mx-auto shadow-sm">
                  <Icon name="Shield" size={24} className="text-blue-600 dark:text-blue-400" />
                </div>
                <p className="text-sm font-bold text-foreground">{selectedMatch.team2Name}</p>
              </div>
            </div>
            <p className="text-xs text-center text-muted-foreground">{selectedMatch.tournamentName}</p>
          </div>

          {/* Date, Time, Venue */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="bg-muted/50 rounded-lg p-3">
              <div className="flex items-center mb-1">
                <Icon name="Calendar" size={14} className="text-muted-foreground me-2" />
                <p className="text-xs font-medium text-foreground">{t('Date')}</p>
              </div>
              <p className="text-sm text-foreground">{formatDate(selectedMatch.scheduledDateTime)}</p>
            </div>
            <div className="bg-muted/50 rounded-lg p-3">
              <div className="flex items-center mb-1">
                <Icon name="Clock" size={14} className="text-muted-foreground me-2" />
                <p className="text-xs font-medium text-foreground">{t('Time')}</p>
              </div>
              <p className="text-sm text-foreground">{formatTime(selectedMatch.scheduledDateTime)}</p>
            </div>
          </div>

          <div className="bg-muted/50 rounded-lg p-3 mb-4">
            <div className="flex items-center mb-1">
              <Icon name="MapPin" size={14} className="text-muted-foreground me-2" />
              <p className="text-xs font-medium text-foreground">{t('Venue')}</p>
            </div>
            <p className="text-sm text-foreground">{selectedMatch.location}</p>
          </div>

          {/* Required Certification */}
          {selectedMatch.requiredCertification !== undefined && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 mb-4">
              <div className="flex items-center">
                <Icon name="Award" size={14} className="text-blue-600 dark:text-blue-400 me-2" />
                <div>
                  <p className="text-xs text-blue-700 dark:text-blue-300">{t('Required Certification')}</p>
                  <p className="text-sm font-semibold text-blue-900 dark:text-blue-200">
                    {t(certificationKeys[(resolveEnum(RefereeCertification, selectedMatch.requiredCertification) as RefereeCertification)] ?? 'Unknown')}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Current Assignment Status */}
          {selectedMatch.refereeTeamMembers?.length > 0 ? (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3 mb-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center">
                  <Icon name="Users" size={14} className="text-green-600 dark:text-green-400 me-2" />
                  <p className="text-xs text-green-700 dark:text-green-300">{t('Referee Team Members')}</p>
                </div>
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300">
                  <Icon name="CheckCircle" size={12} className="me-1" />
                  {t('Assigned ({{count}})', { count: selectedMatch.refereeTeamMembers.length })}
                </span>
              </div>
              <div className="space-y-1.5">
                {selectedMatch.refereeTeamMembers.map((member) => (
                  <div key={member.id} className="flex items-center text-sm">
                    <Icon name="User" size={12} className="text-green-600 dark:text-green-400 me-2" />
                    <span className="font-medium text-green-900 dark:text-green-200">{member.userName}</span>
                    <span className="text-green-700 dark:text-green-400 ms-1.5 text-xs">({t(roleKeys[(resolveEnum(MatchRole, member.role) as MatchRole) ?? member.role] ?? 'Unknown')})</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="bg-muted/50 border border-border rounded-lg p-3 mb-4">
              <div className="flex items-center">
                <Icon name="AlertCircle" size={14} className="text-muted-foreground me-2" />
                <div>
                  <p className="text-xs text-foreground">{t('Assignment Status')}</p>
                  <p className="text-sm font-semibold text-foreground">{t('No referee assigned')}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MatchDetailsPanel;
